import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Upload, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { UPLOAD_STAGES } from "@/lib/reportVaultConstants";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

// Staff-only "select category + files, upload" form for the Report Vault tab.
export function ReportUploadForm({ uploadCategory, setUploadCategory, pendingFiles, handleFilesSelected, isUploading, startUpload }) {
  const isReadOnly = useIsGracePeriod();
  return (
    <div className="mt-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      {isReadOnly && (
        <p className="w-full text-xs text-amber-700" data-testid="report-upload-readonly-note">
          Report upload is disabled while the clinic's subscription is in its grace period.
        </p>
      )}
      <div>
        <Label>Category</Label>
        <Select value={uploadCategory} onValueChange={setUploadCategory} disabled={isUploading || isReadOnly}>
          <SelectTrigger data-testid="upload-category-select" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="lab_report">Lab Report</SelectItem>
            <SelectItem value="previous_report">Previous Report</SelectItem>
            <SelectItem value="previous_prescription">Previous Prescription</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>File(s) — PDF/JPG/PNG, max 10MB each</Label>
        <Input data-testid="upload-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" disabled={isUploading || isReadOnly} onChange={handleFilesSelected} />
        {pendingFiles.length > 0 && (
          <p className="mt-1 text-xs text-slate-500" data-testid="pending-files-count">{pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""} selected</p>
        )}
      </div>
      <Button data-testid="upload-report-btn" disabled={pendingFiles.length === 0 || isUploading || isReadOnly} className="gap-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-50" onClick={startUpload}>
        {isUploading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</>
        ) : (
          <><Upload className="h-4 w-4" /> Upload {pendingFiles.length > 1 ? `${pendingFiles.length} Files` : ""}</>
        )}
      </Button>
    </div>
  );
}

// Live progress panel for the background upload + AI-analysis queue.
export function ReportUploadQueuePanel({ uploadQueue, isUploading, dismissQueue }) {
  return (
    <div className="mt-4 rounded-lg border border-teal-100 bg-teal-50/50 p-4" data-testid="upload-progress-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Report Processing Queue</p>
        {!isUploading && (
          <button data-testid="dismiss-upload-queue-btn" onClick={dismissQueue} className="text-xs text-slate-500 hover:text-slate-800">Dismiss</button>
        )}
      </div>
      <div className="mt-2 space-y-2">
        {uploadQueue.map((item) => (
          <div key={item.localId} className="flex items-center gap-2 text-sm" data-testid={`upload-queue-item-${item.localId}`}>
            {item.status === "completed" ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : item.status === "failed" ? (
              <XCircle className="h-4 w-4 shrink-0 text-red-600" />
            ) : (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-teal-700" />
            )}
            <span className="truncate font-medium text-slate-800">{item.fileName}</span>
            <span className="text-slate-400">—</span>
            <span className={item.status === "failed" ? "text-red-600" : "text-slate-600"}>
              {item.status === "failed" ? item.error : UPLOAD_STAGES[item.stageIndex]}
              {item.status !== "completed" && item.status !== "failed" ? "..." : item.status === "completed" ? " ✓" : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
